import React, { PropsWithChildren, ReactElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ButtonTypes,
  MenuItemModalProps,
  Modifier,
  ModifierGroupInterface,
  ProductInterface,
} from '../../services/exports/Interfaces';
import CustomButton from '../../components-library/buttons/CustomButton';
import CounterButton from '../../components-library/buttons/CounterButton';
import CustomNavBar from '../../components-library/navigation/CustomNavBar';
import useScreenType from '../../hooks/utility/useScreenType';
import { MODAL_SIZES, OfferKindEnum } from '../../services/exports/Constants';
import CustomModal from '../../components-library/CustomModal';
import useOrderingDisabledStatus from '../../hooks/availability/useOrderingDisabledStatus';
import PriceComparison from '../../components-library/menu/PriceComparison';
import Theme from '../../components-library/Theme';
import ProductIdentifiers from '../../components-library/menu/product/ProductIdentifiers';
import useProductIdentifiers from '../../hooks/menu/useProductIdentifiers';
import useTheme from '../../hooks/ui/useTheme';
import { useTranslation } from 'react-i18next';
import ButtonFooter from '../../components-library/footers/ButtonFooter';
import useAppState from '../../hooks/global/useAppState';
import useMenu from '../../hooks/menu/useMenu';
import useBasket from '../../hooks/basket/useBasket';
import TextTransformationResource from '../../services/resources/TextTransformationResource';
import collect, { Collection } from 'collect.js';
import useAppMethods from '../../hooks/utility/useAppMethods';
import TextArea from '../../components-library/inputs/TextArea';
import SeoResource from '../../services/resources/SeoResource';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import EarnCashbackTag from '../../components-library/cashback/EarnCashbackTag';
import ModifierGroup from '../../components-library/menu/ModifierGroup';
import MenuItemModal from './MenuItemModal';
import useMenuItemModifiers from './useMenuItemModifiers';
import BasketHelpers from '../../services/helpers/BasketHelpers';
import { BasketLineItem, BasketLineItemModifier } from '../../store/types';
import useAllergens from '../../hooks/menu/useAllergens';
import classnames from 'classnames';
import HeartIcon from '../../assets/logo/HeartIcon';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useProductLikes from '../../hooks/global/useProductLikes';
import { isNumber } from 'lodash';
import { CompiledModifier, CompiledProduct } from '../../providers/MenuProvider';
import toast from 'react-hot-toast';
import useFreeItemOffer from '../../features/offers/hooks/useFreeItemOffer';
import useAvailableFreeProducts from '../../features/offers/hooks/useAvailableFreeProducts';
import useOffersStuff from '../../features/offers/hooks/useOffersStuff';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import { Modals } from '../../modules/mobile/exports/Enums';
import useBogoItemOffer from '../../features/offers/hooks/useBogoItemOffer';
import TrustLabel from '../../components-library/labels/TrustLabel';
import NutritionInfoBreakdown from './NutritionInfoBreakdown';
import useMenuItemPrice from './useMenuItemPrice';

const MenuItemsModal = (props: Partial<MenuItemModalProps>) => {
  const { hasHeader = true } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Modals:MenuItemsModal' });

  const { menu, menuItemModalProps } = useAppState();
  const { screenPosition } = menu;
  const {
    product: productProp,
    onCloseModal,
    lineItem: lineItemProp,
    basketItemIndex,
    showProductAddedToast,
    onBack,
    offer,
  } = collect(props).keys().isNotEmpty() ? props : menuItemModalProps;

  const { isDesktop, isMobile } = useScreenType();
  const theme = useTheme();
  const { compileProduct, getUpsellModifierGroup } = useMenu();
  const { addItem, items, updateItem } = useBasket();
  const { render: renderOrderingDisabled } = useOrderingDisabledStatus();
  const { toggleMenuItemModal, toggleOrderBasketModal, toggleFreeItemsModal } = useAppMethods();
  const { trackViewItem } = useAnalytics();
  const { isMobileApp } = useMobileApp();
  const { openModal } = useModalRouter();
  // Memoized because compileProduct returns a fresh object each call, and an unstable `product`
  // would re-run every derived memo below it on each scroll frame.
  const product = useMemo(() => compileProduct(productProp?.id), [compileProduct, productProp?.id]);
  const { decoratedName } = useProductIdentifiers(product);
  const freeItemOffer = useFreeItemOffer();
  const availableFreeProducts = useAvailableFreeProducts(freeItemOffer?.offer);
  const { appliedOffers } = useOffersStuff();
  const bogoItemOffer = useBogoItemOffer();
  const isBogoItem = bogoItemOffer?.isEligible && bogoItemOffer.isEligible(product);
  const hasFreeItemOffer = freeItemOffer?.isEligible && freeItemOffer.isEligible(product);
  const isFreeItemOfferApplied = !!appliedOffers?.some?.(
    (appliedOffer) => appliedOffer.id === freeItemOffer?.offer?.id,
  );
  const upsellModifierGroup = getUpsellModifierGroup(items, product);
  const hasUpsellModifierGroup = !!upsellModifierGroup?.modifiers?.length;

  const [lineItem, setLineItem] = useState<BasketLineItem>(() =>
    lineItemProp
      ? structuredClone(lineItemProp)
      : {
          product_id: product?.id,
          quantity: isBogoItem ? 2 : 1,
          modifiers: [],
        },
  );
  const closeAfterAddTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const { subItem, openSubItem, closeSubItem, getSubLineItem } = useMenuItemModifiers({ scrollToModifierGroup });
  const { isAllergensOpened, setIsAllergensOpened, toggleAllergensOpened, AllergensToggleButton, NutritionInfo } =
    useAllergens();

  const updateLineItem = (field, value) =>
    setLineItem((current) => ({
      ...current,
      [field]: value,
    }));
  const { price, originalPrice } = useMenuItemPrice(lineItem, offer);

  const [scrollY, setScrollY] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(!!product?.image_url);
  const [loadingNavigation, setLoadingNavigation] = useState<boolean>(false);
  const [showValidation, setShowValidation] = useState<boolean>(false);

  const invalidModifierGroupIds = useMemo<Set<number>>(() => {
    if (!showValidation) {
      return new Set();
    }

    return new Set(
      collect(product?.modifier_groups ?? [])
        .filter((group: ModifierGroupInterface) => BasketHelpers.isModifierGroupRequired(group))
        .filter((group: ModifierGroupInterface) =>
          collect(lineItem.modifiers).where('modifier_group_id', group.id).isEmpty(),
        )
        .map((group: ModifierGroupInterface) => group.id)
        .all() as number[],
    );
  }, [showValidation, product?.modifier_groups, lineItem.modifiers]);

  const headerRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLDivElement>(null);

  const [headerHeight, setHeaderHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.clientHeight);
    }
  }, [product?.image_url]);

  const scrollYRef = useRef<number>(scrollY);
  scrollYRef.current = scrollY;

  const [modifierGroupRefs] = useState<Collection<React.RefObject<HTMLDivElement>>>(() =>
    collect(product?.modifier_groups ?? [])
      .when(hasUpsellModifierGroup, (collection) => collection.push(upsellModifierGroup))
      .keyBy('id')
      .map(() => React.createRef()),
  );

  function scrollToModifierGroup(id: number) {
    const ref = modifierGroupRefs.get(id);

    ref?.current && ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  const toggleModifier = (entry: ProductInterface | Modifier, modifierGroup: ModifierGroupInterface): void => {
    const _lineItem = BasketHelpers.toggleModifier({
      lineItem: lineItem,
      modifierGroup,
      entry,
    });

    _lineItem && setLineItem(_lineItem);
  };

  function validateInput() {
    const valid = BasketHelpers.validateModifiers(product, lineItem.modifiers);

    if (!valid) {
      const invalidModifierGroup = BasketHelpers.findInvalidModifierGroup(product, lineItem.modifiers);

      setShowValidation(true);
      invalidModifierGroup && scrollToModifierGroup(invalidModifierGroup.id);

      return;
    }

    if (hasUpsellModifierGroup) {
      const upsellItems = lineItem.modifiers?.filter(
        (modifier) => modifier.modifier_group_id === upsellModifierGroup.id,
      );

      upsellItems.forEach((item) =>
        addItem({
          product_id: item.modifier_id,
          quantity: item.quantity,
          modifiers: item.modifiers,
        }),
      );
    }

    const nonUpsellModifiers = upsellModifierGroup?.id
      ? lineItem.modifiers?.filter((modifier) => modifier.modifier_group_id !== upsellModifierGroup.id)
      : lineItem.modifiers;

    if (isNumber(basketItemIndex)) {
      updateItem(basketItemIndex, { ...lineItem, modifiers: nonUpsellModifiers });
      goBack();

      return;
    }

    const added = addItem({ ...lineItem, modifiers: nonUpsellModifiers });

    if (!added) {
      toast.error(t('error_messages.item_not_added'));

      return;
    }

    setLoadingNavigation(true);
    closeAfterAddTimerRef.current = setTimeout(() => {
      setLoadingNavigation(false);
      showProductAddedToast && toast.success(t('success_messages.item_added'));
      if (isDesktop) toggleOrderBasketModal({ openModal: true });
      goBack();

      if (
        hasFreeItemOffer &&
        freeItemOffer?.offer?.kind === OfferKindEnum.FreeItemByProducts &&
        !isFreeItemOfferApplied &&
        availableFreeProducts.length > 0
      ) {
        openFreeItemsModal();
      }
    }, 500);
  }

  function openFreeItemsModal() {
    if (!isMobileApp) {
      toggleFreeItemsModal({ openModal: true, offer: freeItemOffer.offer });

      return;
    }

    openModal({
      id: Modals.FreeItemsModal,
      props: {
        offer: freeItemOffer.offer,
      },
    });
  }

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 0);
  }, []);

  // The close-after-add delay must not outlive this modal: on mobile the router holds one
  // modal at a time, and a timer firing after unmount would close whichever modal replaced it.
  useEffect(() => () => clearTimeout(closeAfterAddTimerRef.current), []);

  useEffect(() => {
    trackViewItem(product);
  }, []);

  function goBack(onBackAction = false): void {
    closeModal();
    setIsAllergensOpened(false);
    onCloseModal && onCloseModal();

    onBackAction && onBack && onBack();
  }

  function closeModal(): void {
    toggleMenuItemModal({
      openModal: false,
      product: null,
    });
  }

  function handleScroll(y: number): void {
    setScrollY(y);
  }

  const renderNote = useMemo<ReactElement>(() => {
    function onFocus(): void {
      if (isDesktop) {
        setTimeout(() =>
          noteRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          }),
        );
      }
    }

    return (
      <div>
        <div className="bg-gray-500 dark:bg-brand-card px-md py-xs">
          <h6>{t('note.title')}</h6>
          <p className="text-muted-foreground text-xs mt-[2px]">{t('note.description')}</p>
        </div>
        <div className="mt-sm px-md" ref={noteRef}>
          <TextArea
            value={lineItem.note}
            placeholder={t('note.placeholder')}
            maxLength={LINE_ITEM_NOTE_MAX_LENGTH}
            onChange={(value) => updateLineItem('note', new TextTransformationResource(value).reduceToText())}
            autoFocus={false}
            onFocus={onFocus}
            rows={5}
          />
        </div>
      </div>
    );
  }, [lineItem.note, theme, screenPosition, isMobile, noteRef.current]);

  const renderProductInfo = (): ReactElement => {
    return (
      <div className="pb-md px-md">
        {/* Machine-readable mirror of the CTA prices, for e2e assertions. Lives here rather than in
            the CTA so it also exists on the zero-price and unavailable branches. */}
        <span
          data-test="menu-item-price"
          data-actual-price={price}
          data-original-price={originalPrice}
          className="hidden"
        />
        <div className="flex gap-xs lg:items-center items-start mb-sm relative">
          <h4 className="break-words shrink">{decoratedName}</h4>
        </div>
        <div className="space-y-xs">
          <NutritionInfoBreakdown product={product} />
          <MenuItemsModal.Likes product={product} />
          <ProductIdentifiers product={product} />
          <p className="text-sm text-muted-foreground break-words">{product?.description}</p>
        </div>
        <EarnCashbackTag amount={price} className="mt-sm" />
        <TrustLabel className="mt-sm" />
        {!!product?.nutrition_info && (
          <AllergensToggleButton className="mt-sm" isOpen={isAllergensOpened} toggleAllergens={toggleAllergensOpened} />
        )}
      </div>
    );
  };

  const renderCounterButton = (): ReactElement => {
    return (
      <CounterButton
        classes={{
          container: 'h-[43px]',
          buttons: '!w-4',
        }}
        iconColor={theme.color.brand.text.secondary}
        count={lineItem.quantity}
        increase={() => updateLineItem('quantity', lineItem.quantity + 1)}
        decrease={() => updateLineItem('quantity', lineItem.quantity - 1)}
      />
    );
  };

  const renderAddButton = (): ReactElement => {
    if (renderOrderingDisabled) {
      return renderOrderingDisabled;
    }

    if (!product?.is_available) {
      return (
        <div className={productUnavailableContainer}>
          <h4 className={productUnavailableTextStyle}>{t('error_messages.product_unavailable')}</h4>
        </div>
      );
    }

    if (product?.actual_price === 0) {
      return (
        <ButtonFooter className={buttonContainer}>
          {/* Same testId as the priced branch below: both are "the button that adds this product",
              and a spec must not have to know which pricing branch rendered. */}
          <CustomButton
            testId="menu-item-add-button"
            title={t('buttons.add_to_card')}
            onClick={validateInput}
            loading={loadingNavigation}
          />
        </ButtonFooter>
      );
    }

    const buttonTitle = isNumber(basketItemIndex) ? t('buttons.update_item') : t('buttons.add_to_card');

    return (
      <ButtonFooter className={addButtonContainer}>
        <div className="lg:grid lg:grid-cols-12 flex gap-[10px] items-center">
          <div className="lg:col-span-4 shrink-none min-w-[100px]">{renderCounterButton()}</div>
          <div className="lg:col-span-8 flex-1 shrink-0">
            <CustomButton
              // The only way a product with modifiers enters the basket, and the trigger for the
              // required-group validation (`validateInput` sets showValidation and scrolls to the
              // offending group). Its label is translated AND switches between add/update, so it
              // cannot be located by text.
              testId="menu-item-add-button"
              title={
                <div className="flex items-center justify-between gap-1 w-full">
                  <span className="text-brand-button-primary-text line-clamp-1 text-sm">{buttonTitle}</span>
                  <PriceComparison
                    actualPrice={price}
                    originalPrice={originalPrice}
                    originalPriceStyle={{
                      fontSize: Theme.font.size.sm,
                      color: theme.buttons[ButtonTypes.primary].text,
                    }}
                    actualPriceStyle={{
                      fontSize: Theme.font.size.sm,
                      color: theme.buttons[ButtonTypes.primary].text,
                    }}
                  />
                </div>
              }
              onClick={validateInput}
              loading={loadingNavigation}
            />
          </div>
        </div>
      </ButtonFooter>
    );
  };

  const renderHeaderBar = useMemo(() => {
    if (!hasHeader) return null;

    const shouldFadeInHeader = isMobileApp || scrollYRef.current > (headerRef.current?.clientHeight ?? 0);

    return (
      <div className={headerContainer} ref={headerRef}>
        {!!product?.image_url && <div className={headerBackground(shouldFadeInHeader)} />}
        <CustomNavBar
          rightClick={() => goBack(true)}
          hideBackIcon
          containerClassName={classnames({ 'bg-brand-background-primary py-sm': !product?.image_url })}
          transparent={!!product?.image_url}
        />
      </div>
    );
  }, [isDesktop, product?.image_url, scrollYRef.current, headerRef.current, hasHeader, isMobileApp]);

  const renderProductPicture = useMemo((): ReactElement | null => {
    return product?.image_url ? (
      <div
        className={productImageContainer}
        style={{
          marginTop: -headerHeight,
        }}
      >
        <img
          src={product?.thumbnail_url ?? product?.image_url}
          alt={SeoResource.toAltText(`${product?.name} image`)}
          className={productImage}
        />
      </div>
    ) : null;
  }, [product?.image_url, scrollYRef.current, headerHeight, isMobileApp, product?.likes_count]);

  const updateSubLineItem = (entryLineItem: BasketLineItemModifier): void => {
    const _lineItem = BasketHelpers.toggleModifier({
      lineItem,
      modifierGroup: subItem.modifierGroup,
      entry: subItem.item,
      entryLineItem,
    });

    _lineItem && setLineItem(_lineItem);
  };

  const adjustQuantity = (
    modifierGroup: ModifierGroupInterface,
    entry: Modifier | ProductInterface,
    step: number,
  ): void => {
    const _lineItem = BasketHelpers.adjustQuantity({
      lineItem: lineItem,
      entry,
      modifierGroup,
      step,
    });

    _lineItem && setLineItem(_lineItem);
  };

  const getSelectedModifiers = (modifierGroup: ModifierGroupInterface): BasketLineItemModifier[] =>
    lineItem.modifiers?.filter((item) => item.modifier_group_id === modifierGroup.id);

  return (
    <Wrapper fullScreen={!isDesktop} goBack={() => goBack(true)} onScroll={handleScroll} loading={loading}>
      {subItem ? (
        <MenuItemModal
          data={subItem.item}
          selectedLineItem={getSubLineItem(lineItem)}
          scrollY={scrollY}
          goBack={closeSubItem}
          updateParentLineItem={updateSubLineItem}
        />
      ) : (
        <>
          <div className={modalContainer}>
            {renderHeaderBar}
            {renderProductPicture}
            <div className={wrapper(!!product?.image_url)}>
              {renderProductInfo()}
              {isAllergensOpened ? (
                <NutritionInfo data={product} className="px-md" />
              ) : (
                <>
                  <div className="mb-md">
                    {product?.modifier_groups?.map((modifierGroup) => (
                      <ModifierGroup
                        data={modifierGroup as ModifierGroupInterface<CompiledModifier>}
                        selectedModifiers={getSelectedModifiers(modifierGroup)}
                        toggle={(entry: CompiledProduct | CompiledModifier) => toggleModifier(entry, modifierGroup)}
                        key={`modifier-group-${modifierGroup.id}`}
                        ref={modifierGroupRefs.get(modifierGroup.id)}
                        openSubItem={(item: CompiledProduct) => openSubItem(item, modifierGroup)}
                        adjustQuantity={(entry, step) => adjustQuantity(modifierGroup, entry, step)}
                        hasError={invalidModifierGroupIds.has(modifierGroup.id)}
                      />
                    ))}
                    {renderNote}
                  </div>

                  {hasUpsellModifierGroup && (
                    <ModifierGroup
                      isUpsell
                      data={upsellModifierGroup as any as ModifierGroupInterface<CompiledModifier>}
                      selectedModifiers={getSelectedModifiers(upsellModifierGroup)}
                      toggle={(entry: CompiledProduct | CompiledModifier) => toggleModifier(entry, upsellModifierGroup)}
                      ref={modifierGroupRefs.get(upsellModifierGroup.id)}
                      openSubItem={(item: CompiledProduct) => openSubItem(item, upsellModifierGroup)}
                      adjustQuantity={(entry, step) => adjustQuantity(upsellModifierGroup, entry, step)}
                    />
                  )}
                </>
              )}
            </div>
            {!isAllergensOpened && renderAddButton()}
          </div>
        </>
      )}
    </Wrapper>
  );
};

interface WrapperProps extends PropsWithChildren {
  goBack: () => void;
  onScroll: (y: number) => void;
  loading: boolean;
  fullScreen: boolean;
}

function Wrapper(props: WrapperProps) {
  const { children, goBack, onScroll, loading, fullScreen } = props;
  const { isMobileApp } = useMobileApp();

  if (isMobileApp) {
    return <>{children}</>;
  }

  return (
    <CustomModal
      open={true}
      size={MODAL_SIZES.SM}
      fullScreen={fullScreen}
      onClose={goBack}
      onScroll={onScroll}
      loading={loading}
    >
      {children}
    </CustomModal>
  );
}

export default MenuItemsModal;

const modalContainer = 'bg-brand-background-primary size-full relative flex-col flex';

const headerContainer = 'sticky top-0 z-10 relative';

const headerBackground = (fadeIn: boolean) =>
  `bg-brand-background-primary transition-opacity duration-500 ease-out border-b absolute inset-0 ${fadeIn ? 'opacity-100' : 'opacity-0'}`;

// The backend rejects a longer item note at /order-validations (`line_items.*.note` max:255), which
// the customer would only find out about in the cart (SHOP-WEB-5E).
const LINE_ITEM_NOTE_MAX_LENGTH = 255;

const productImageContainer = 'relative w-full';

const productImage = 'object-cover size-full lg:h-[320px] w-full';

const wrapper = (hasOffset: boolean) =>
  `pt-md lg:pb-md pb-[206px] md:pb-[148px] ${hasOffset ? 'border-t' : ''} bg-brand-background-primary relative flex-1`;

const buttonContainer = `lg:sticky fixed bottom-0`;

const addButtonContainer = `lg:sticky fixed bottom-0 py-sm space-y-8`;

const productUnavailableContainer =
  'lg:sticky fixed bottom-0 w-full flex flex-row items-center justify-center px-lg py-md bg-gray border-t';

const productUnavailableTextStyle = 'text-muted-foreground text-center';

MenuItemsModal.Likes = ({ product }: { product: ProductInterface }): ReactElement => {
  const { toggleProductLike, isLiked } = useProductLikes();
  const liked = Boolean(product) && isLiked(product.id);

  return (
    Boolean(product) && (
      <div
        onClick={(e) => {
          e.stopPropagation();
          toggleProductLike(product.id);
        }}
        className="text-muted-foreground w-fit cursor-pointer text-sm font-medium flex items-center space-x-1"
      >
        <HeartIcon
          filled={liked}
          className={classnames('h-3', {
            'fill-danger': liked,
            'fill-gray-700': !liked,
          })}
        />
        <span className="text-xs text-muted-foreground">{product.likes_count}</span>
      </div>
    )
  );
};
